import React, { Fragment, ReactNode } from 'react';
import { ExtendableProps } from '../_internal/components';
import {
  ItemRenderData,
  MinOptionInfo,
  SelectBaseProps,
} from '../_internal/selectBase/types';
import useUniqueId from '../_internal/useUniqueId';

export interface MultiSelectBaseProps<
  // `ValueString` makes life easier for consumer when they use an enum
  // for options. This way when `onChange` fires they'll get an array of
  // enum values instead of an array of strings
  ValueString extends string = string,
  // `OptionInfo` ensures that we get an object with at least `value`
  // (and optional `disabled`) defined on it
  OptionInfo extends MinOptionInfo<ValueString> = MinOptionInfo<ValueString>,
> extends SelectBaseProps<
    ValueString,
    OptionInfo,
    ItemRenderData<ValueString, 'checkbox'>
  > {
  /**
   * A handler that is triggered when the component's selected options change
   */
  onChange: (value: ValueString[]) => void;
  /**
   * The current selected value(s) of the component
   * @ignore The conversion to React PropTypes is busted because of the array of
   * generics, so just exclude
   */
  value: ValueString[];
}

/**
 * Allows for the props of a parent component to extend
 * the props of `MultipleSelectBase` *before* they are merged with the
 * props of the underlying element
 */
export type ExtendableMultiSelectBaseProps<
  ValueString extends string,
  OptionInfo extends MinOptionInfo<ValueString>,
  OverrideProps = {},
> = ExtendableProps<
  MultiSelectBaseProps<ValueString, OptionInfo>,
  OverrideProps
>;

// The default container does nothing (returns back the contents)
const DEFAULT_CONTAINER = (children: ReactNode) => children;

/**
 * `MultiSelectBase` is a headless / display-free base component. Use it
 * as a building block for creating components that allow for multiple
 * selected options from a set of options. It manages the selected value state
 * and core accessibility, while offloading visual display of the selectable
 * UI to the user via the `children` render prop. Example UIs: checkbox
 * group, multi-select button group, etc.
 */
const MultiSelectBase = <
  ValueString extends string,
  OptionInfo extends MinOptionInfo<ValueString>,
>({
  name,
  idPrefix: initialIdPrefix,
  value,
  options,
  children,
  container = DEFAULT_CONTAINER,
  onChange,
  disabled = false,
}: MultiSelectBaseProps<ValueString, OptionInfo>) => {
  const uniqueId = useUniqueId(name);
  const idPrefix = initialIdPrefix || uniqueId;
  const selectedValues = new Set(value);

  const renderedOptions = options.map((currentOption, index) => {
    const { value: optionValue, disabled: optionDisabled = false } =
      currentOption;
    const renderedOption = children(
      {
        value: optionValue,
        id: `${idPrefix}-${optionValue}`,
        name,
        like: 'checkbox',
        isSelected: selectedValues.has(optionValue),
        disabled: disabled || optionDisabled,
        onChange: isSelected => {
          // act like a checkbox by toggling selected when clicked
          if (isSelected) {
            selectedValues.add(optionValue);
          } else {
            selectedValues.delete(optionValue);
          }

          // convert back to an array before passing to `onChange`
          onChange([...selectedValues]);
        },
      },
      // passing through the option they passed so that they can read
      // any additional properties they added (like `label`) needed
      // for rendering
      currentOption,
      index,
    );

    // The fragment is needed here in order to add a `key` prop
    // for each option
    return <Fragment key={optionValue}>{renderedOption}</Fragment>;
  });

  // the need for the fragment is because the container returns a
  // `React.Node` and it can be multiple elements. We need the return
  // value from the component to be a single parent element.
  return <>{container(renderedOptions)}</>;
};

export default MultiSelectBase;
